/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { buildDetachTargetsPayload, PRODUCT_VIEW_LAYER } from '../../components/AssetTargetsDialog/helpers/assetProductLinks';
import l10n from '../../shared/strings';
import { fetchVariantAssets } from './fetchVariantAssets';

const LS_KEY = 'picsio.hideDetachFromVariantWarning';
const strings = l10n.DETAILS.confirmDetachFromVariant;

export const removeAssetFromVariant = createAsyncThunk(
  'products/removeAssetFromVariant',
  async ({
    assetId,
    productId,
    variantId,
    masterVariantId,
    channelId,
    variantType = 'masterVariant',
    listingId,
    layer,
  }, {
    dispatch,
    extra: {
      sdk, Logger, utils, showErrorDialog, showDialogAsync,
    },
  }) => {
    try {
      if (variantType === 'listingVariant' && layer === PRODUCT_VIEW_LAYER.LIVE) {
        return false;
      }

      if (!utils.LocalStorage.get(LS_KEY)) {
        try {
          await showDialogAsync({
            title: strings.title,
            text: strings.text,
            checkbox: {
              label: strings.checkboxLabel,
              value: false,
              onChange: (value) => {
                utils.LocalStorage.set(LS_KEY, value);
              },
            },
            textBtnOk: strings.textBtnOk,
          }).promise;
        } catch (error) {
          /** User pressed "Cancel" */
          return false;
        }
      }

      const targets = variantType === 'listingVariant'
        ? {
          listings: [{
            listingId,
            listingVariantIds: [variantId],
          }],
        }
        : {
          masterVariantIds: [variantId],
        };

      await sdk.assets.detachFromTargets(
        [assetId],
        buildDetachTargetsPayload({ productId, targets }),
      );

      if (variantType === 'listingVariant' && channelId && masterVariantId) {
        await dispatch(fetchVariantAssets({
          productId,
          variantId,
          channelId,
          masterVariantId,
        }));
      } else {
        await dispatch(fetchVariantAssets({ variantId, productId }));
      }

      return true;
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not remove asset from product variant';
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error remove asset from product variant'), { error }, [
        'RemoveAssetFromProductVariantFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      removeAssetFromVariant.pending,
      () => {
        /** Do nothing */
      },
    )
    .addCase(
      removeAssetFromVariant.fulfilled,
      () => {
        /** Variant assets refreshed via fetchVariantAssets */
      },
    )
    .addCase(
      removeAssetFromVariant.rejected,
      (state, { error }) => {
        state.error = error;
      },
    );
};
